// ---------------------------------------------------------------------------
// YouAreYou (uru) — Contacts lib
//
// Thin wrapper around @capacitor-community/contacts.
// Normalises phone numbers to E.164 for matching against YouAreYou data.
//
// All exported functions are no-ops on web (isNative === false).
// ---------------------------------------------------------------------------

import { Contacts, PhoneType } from '@capacitor-community/contacts'
import type { ContactPayload } from '@capacitor-community/contacts'
import { isNative } from '@/lib/platform'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface DeviceContact {
  contactId: string
  displayName: string
  /** Normalised E.164 phone numbers for this contact (may be multiple). */
  phones: string[]
}

export interface NewContactInput {
  firstName: string
  lastName: string
  /** E.164 phone number */
  phone: string
}

// ---------------------------------------------------------------------------
// Phone normalisation
// ---------------------------------------------------------------------------

/**
 * Attempt to normalise a phone number to E.164 format.
 * This is a best-effort client-side normalisation for contact matching only —
 * all API calls use server-side normalisation.
 *
 * Handles:
 *   - Already E.164: "+15551234567" → "+15551234567"
 *   - US 10-digit: "5551234567" → "+15551234567"
 *   - Formatted:   "(555) 123-4567" → "+15551234567"
 *   - International without +: "15551234567" → "+15551234567" (if 11 digits, starts with 1)
 *
 * Returns null if the number cannot be normalised.
 */
export function normalisePhone(raw: string | null | undefined): string | null {
  if (!raw) return null
  // Strip everything except digits and leading +
  const stripped = raw.replace(/[^\d+]/g, '')
  if (stripped.startsWith('+')) {
    // Already E.164 — validate minimum length
    return stripped.length >= 7 ? stripped : null
  }
  const digits = stripped.replace(/\D/g, '')
  if (digits.length === 10) {
    // US/Canada 10-digit
    return `+1${digits}`
  }
  if (digits.length === 11 && digits.startsWith('1')) {
    // US/Canada 11-digit with country code
    return `+${digits}`
  }
  if (digits.length > 7) {
    // International — prepend + and hope for the best
    return `+${digits}`
  }
  return null
}

// ---------------------------------------------------------------------------
// Permission
// ---------------------------------------------------------------------------

/**
 * Request contacts permission.
 * Returns true if permission was granted, false otherwise.
 * Always returns false on web.
 */
export async function requestContactsPermission(): Promise<boolean> {
  if (!isNative) return false
  try {
    const { contacts } = await Contacts.requestPermissions()
    return contacts === 'granted'
  } catch {
    return false
  }
}

/**
 * Check contacts permission without prompting.
 * Returns true if already granted, false otherwise.
 */
export async function checkContactsPermission(): Promise<boolean> {
  if (!isNative) return false
  try {
    const { contacts } = await Contacts.checkPermissions()
    return contacts === 'granted'
  } catch {
    return false
  }
}

// ---------------------------------------------------------------------------
// Read
// ---------------------------------------------------------------------------

/**
 * Fetch all device contacts that have at least one phone number.
 * Returns an empty array on web or if permission is denied.
 */
export async function getDeviceContacts(): Promise<DeviceContact[]> {
  if (!isNative) return []

  const hasPermission = await checkContactsPermission()
  if (!hasPermission) {
    const granted = await requestContactsPermission()
    if (!granted) return []
  }

  try {
    const { contacts } = await Contacts.getContacts({
      projection: {
        name: true,
        phones: true,
      },
    })

    return contacts
      .map((c: ContactPayload): DeviceContact | null => {
        const phones = (c.phones ?? [])
          .map((p) => normalisePhone(p.number))
          .filter((p): p is string => p !== null)

        if (phones.length === 0) return null

        const displayName =
          c.name?.display ??
          [c.name?.given, c.name?.family].filter(Boolean).join(' ') ??
          'Unknown'

        return { contactId: c.contactId, displayName, phones }
      })
      .filter((c): c is DeviceContact => c !== null)
  } catch {
    return []
  }
}

// ---------------------------------------------------------------------------
// Write
// ---------------------------------------------------------------------------

/**
 * Open the native contact creation flow pre-filled with YouAreYou connection data.
 * On web, this is a no-op.
 */
export async function createDeviceContact(input: NewContactInput): Promise<void> {
  if (!isNative) return

  const hasPermission = await checkContactsPermission()
  if (!hasPermission) {
    const granted = await requestContactsPermission()
    if (!granted) return
  }

  try {
    await Contacts.createContact({
      contact: {
        name: {
          given: input.firstName,
          family: input.lastName,
        },
        phones: [
          {
            type: PhoneType.Mobile,
            number: input.phone,
          },
        ],
      },
    })
  } catch {
    // Silently fail — user may have cancelled the native sheet
  }
}
